import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Calendar, Cog, CheckSquare, Target as TargetIcon, History, ChevronRight } from 'lucide-react';
import { fetchDashboard, fetchCoachLogsRecent, fetchCoachLog } from '../../lib/api';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { AREE } from '../../constants/areas';

/**
 * Drawer "Parla col coach": seleziona un momento e naviga alla pagina
 * corrispondente. Il coach NON è una chat sempre visibile — vive in 3 momenti
 * + revisione di un obiettivo specifico.
 *
 * Animazione: slide da destra (translateX 100→0), 320ms ease-out cubic-bezier
 * (0.22, 1, 0.36, 1). Overlay fade 0→0.4 stessa durata. Rispetta
 * prefers-reduced-motion (solo fade rapido, niente slide).
 */
const ENTER_MS = 320;
const EXIT_MS = 260;
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Mappatura `momento` salvato in coach_log → percorso applicativo da aprire
// per riprendere quella conversazione (il useCoachChat di quella pagina legge
// `qp.coach.resume.<momento>` da sessionStorage al mount).
const MOMENTO_TO_PATH = {
  setup: '/setup',
  mese: '/mese',
  settimana: '/settimana',
  quick: '/setup',
};

const MOMENTO_LABEL = {
  setup: 'Setup',
  mese: 'Mese',
  settimana: 'Settimana',
  quick: 'Quick',
};

function formatRelativeDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'ora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}g`;
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function CoachDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const [obiettivi, setObiettivi] = useState([]);
  const [pickObiettivo, setPickObiettivo] = useState(false);
  const [recent, setRecent] = useState(null);       // null = loading, [] = vuoto
  const [mounted, setMounted] = useState(false);  // presenza nel DOM
  const [visible, setVisible] = useState(false);  // stato visivo (anima in/out)
  const reduceMotion = prefersReducedMotion();
  const panelRef = useRef(null);

  // Il drawer non aveva gestione Esc: la delega al focus trap (onEscape).
  useFocusTrap(open, panelRef, { onEscape: onClose });

  // Mount/unmount con transizione
  useEffect(() => {
    let t;
    if (open) {
      setMounted(true);
      t = setTimeout(() => setVisible(true), 10);
      fetchDashboard().then((d) => setObiettivi(d.obiettivi || [])).catch(() => {});
      fetchCoachLogsRecent(12)
        .then((r) => setRecent(r.items || []))
        .catch(() => setRecent([]));
      setPickObiettivo(false);
    } else {
      setVisible(false);
      t = setTimeout(() => setMounted(false), reduceMotion ? 100 : EXIT_MS);
    }
    return () => clearTimeout(t);
  }, [open, reduceMotion]);

  if (!mounted) return null;

  const go = (path) => {
    onClose && onClose();
    // attendi il fade-out prima della navigation per evitare snap
    setTimeout(() => navigate(path), reduceMotion ? 0 : 80);
  };

  // Riprendi una conversazione passata: scarica i messaggi, salvali in
  // sessionStorage (chiave letta da useCoachChat al mount), poi naviga alla
  // pagina del momento corrispondente. Il coach mostrerà i messaggi storici +
  // un divider + un messaggio di "ripresa".
  const resumeConversation = async (item) => {
    try {
      const full = await fetchCoachLog(item.id);
      const msgs = (full?.messaggi || []).filter((m) => !m.streaming);
      const resumeKey = `qp.coach.resume.${item.momento}`;
      sessionStorage.setItem(resumeKey, JSON.stringify({ messaggi: msgs, source_id: item.id }));
      const targetPath = MOMENTO_TO_PATH[item.momento] || '/setup';
      go(targetPath);
    } catch {
      // Errore silente — il drawer mostra "Storico" come fallback
      navigate('/coach-history');
      onClose && onClose();
    }
  };

  const overlayStyle = reduceMotion
    ? {
        opacity: visible ? 0.4 : 0,
        transition: `opacity 120ms linear`,
      }
    : {
        opacity: visible ? 0.4 : 0,
        transition: `opacity ${visible ? ENTER_MS : EXIT_MS}ms ${EASE}`,
      };

  const drawerStyle = reduceMotion
    ? {
        opacity: visible ? 1 : 0,
        transition: `opacity 120ms linear`,
        willChange: 'opacity',
      }
    : {
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        transition: `transform ${visible ? ENTER_MS : EXIT_MS}ms ${EASE}`,
        willChange: 'transform',
      };

  return (
    <>
      <div
        className="fixed inset-0 bg-black z-50"
        style={overlayStyle}
        onClick={onClose}
        data-testid="coach-drawer-overlay"
        aria-hidden="true"
      />
      <aside
        ref={panelRef}
        className="fixed top-0 right-0 h-screen w-[min(420px,92vw)] bg-canvas border-l border-line z-50 shadow-xl flex flex-col"
        style={drawerStyle}
        data-testid="coach-drawer"
        role="dialog"
        aria-label="Parla con QuantumCoach"
        aria-modal="true"
      >
        <div className="px-6 py-5 border-b border-line flex items-center justify-between">
          <div>
            <span className="mono-label">QuantumCoach</span>
            <h2 className="font-display text-2xl tracking-tight mt-1">Parla con QuantumCoach</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-soft hover:text-ink"
            data-testid="coach-drawer-close"
            aria-label="Chiudi"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-3">
          <p className="font-body text-sm text-soft mb-2">
            Il coach parte sempre in un momento specifico. Scegli quale.
          </p>

          {!pickObiettivo ? (
            <>
              <RecentSessionsSection
                items={recent}
                onResume={resumeConversation}
                onViewAll={() => go('/coach-history')}
              />
              <MomentButton
                icon={<Cog size={18} strokeWidth={1.5} />}
                title="Setup anno · quarter"
                desc="Tema, numeri importanti, obiettivi, KR."
                onClick={() => go('/setup')}
                testid="coach-momento-setup"
              />
              <MomentButton
                icon={<Calendar size={18} strokeWidth={1.5} />}
                title="Pianifica il mese"
                desc="Reali del mese chiuso, azioni settimanali, capacità."
                onClick={() => go('/mese')}
                testid="coach-momento-mese"
              />
              <MomentButton
                icon={<CheckSquare size={18} strokeWidth={1.5} />}
                title="Review settimanale"
                desc="Valori reali, confidence, retrospettiva, MIT."
                onClick={() => go('/settimana')}
                testid="coach-momento-settimana"
              />
              <MomentButton
                icon={<TargetIcon size={18} strokeWidth={1.5} />}
                title="Rivedi un obiettivo"
                desc="Apri un obiettivo per discuterne KR, azioni, rischi."
                onClick={() => setPickObiettivo(true)}
                testid="coach-momento-obiettivo"
              />
            </>
          ) : (
            <>
              <button
                onClick={() => setPickObiettivo(false)}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-soft hover:text-ink mb-2"
                data-testid="coach-back-to-moments"
              >
                ← Scegli un altro momento
              </button>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-mute mb-2">
                Seleziona l&apos;obiettivo da rivedere
              </p>
              {obiettivi.length === 0 && (
                <p className="font-body text-sm text-mute italic">Nessun obiettivo definito.</p>
              )}
              <ul className="divide-y divide-linesoft border-y border-linesoft">
                {obiettivi.map((o) => {
                  const meta = AREE[(o.area || '').toLowerCase()] || { color: 'var(--qp-text-soft)' };
                  return (
                    <li key={o.id}>
                      <button
                        onClick={() => go(`/obiettivo/${o.id}`)}
                        className="w-full flex items-center gap-3 py-3 text-left hover:bg-linesoft/60 px-2"
                        data-testid={`coach-pick-obiettivo-${o.id}`}
                      >
                        <span className="font-mono text-[10px] text-mute w-6">{String(o.ordine).padStart(2, '0')}</span>
                        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
                        <span className="flex-1 font-body text-sm" style={{ color: meta.color }}>{o.titolo}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-line">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
            Il coach propone operazioni. Tu decidi se salvarle.
          </p>
        </div>
      </aside>
    </>
  );
}

function MomentButton({ icon, title, desc, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 border border-line hover:border-ink hover:bg-linesoft/40 transition-colors group"
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <span className="text-soft group-hover:text-accent shrink-0 mt-0.5">{icon}</span>
        <div className="flex-1">
          <div className="font-display text-base mb-1">{title}</div>
          <div className="font-body text-xs text-soft leading-relaxed">{desc}</div>
        </div>
      </div>
    </button>
  );
}

/**
 * RecentSessionsSection — mostra le ultime conversazioni col coach come
 * shortcut cliccabili. Il click "riprende" la conversazione: il coach apre
 * sulla pagina del momento corretto con i messaggi storici precaricati.
 *
 * - `items === null` → loading (skeleton mute)
 * - `items === []`   → nessuna conversazione salvata (UI nascosta)
 * - lista limitata a 12, ordinata per ricezione DESC
 */
function RecentSessionsSection({ items, onResume, onViewAll }) {
  if (items === null) return null;          // loading silente
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-5" data-testid="coach-recent-sessions">
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-center gap-2">
          <History size={13} strokeWidth={1.5} className="text-soft" />
          <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-soft">
            Ultime conversazioni · {items.length}
          </h3>
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute hover:text-ink transition-colors"
          data-testid="coach-recent-view-all"
        >
          Storico →
        </button>
      </div>
      <ul className="border border-linesoft divide-y divide-linesoft mb-4 max-h-[280px] overflow-y-auto">
        {items.slice(0, 12).map((it) => (
          <li key={it.id}>
            <button
              type="button"
              onClick={() => onResume(it)}
              className="w-full text-left px-3 py-2.5 hover:bg-linesoft/60 transition-colors flex items-start gap-2 group"
              data-testid={`coach-recent-item-${it.id}`}
              title="Riprendi questa conversazione"
            >
              <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-mute shrink-0 mt-0.5 tabular-nums w-12">
                {formatRelativeDate(it.created_at)}
              </span>
              <span
                className={`font-mono text-[9px] uppercase tracking-[0.15em] shrink-0 mt-0.5 px-1 py-px border ${
                  it.esito === 'applicato'
                    ? 'border-ok text-ok'
                    : it.esito === 'annullato'
                      ? 'border-mute text-mute'
                      : 'border-line text-soft'
                }`}
              >
                {MOMENTO_LABEL[it.momento] || it.momento}
              </span>
              <span className="flex-1 font-body text-xs text-ink leading-snug line-clamp-2">
                {it.preview}
              </span>
              <ChevronRight size={12} strokeWidth={1.5} className="text-mute group-hover:text-ink shrink-0 mt-0.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
